import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { AlertController } from '@ionic/angular';
import { environment } from 'src/environments/environment';

interface PlatformVersion {
  latest_version_code: number;
  min_version_code: number;
  store_url: string;
}

interface AppVersionResponse {
  message: string;
  android: PlatformVersion;
  ios: PlatformVersion;
}

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  private presenting = false;
  private readonly snoozeKey = 'app_update_snooze_date';

  constructor(
    private http: HttpClient,
    private alertController: AlertController
  ) {}

  async check(): Promise<void> {
    const platform = Capacitor.getPlatform();
    if (platform !== 'android' && platform !== 'ios') {
      return;
    }

    try {
      const info = await App.getInfo();
      const current = parseInt(info.build, 10);
      if (!current) {
        return;
      }

      const config = await this.http
        .get<AppVersionResponse>(`${environment.apiURI}/app-version`)
        .toPromise();
      if (!config) {
        return;
      }
      const platformConfig = platform === 'ios' ? config.ios : config.android;
      if (!platformConfig) {
        return;
      }

      if (current < Number(platformConfig.min_version_code || 0)) {
        await this.prompt(
          true,
          config.message || 'Please update SchoolUniverse app to continue.',
          platformConfig.store_url
        );
        return;
      }

      if (
        current < Number(platformConfig.latest_version_code || 0) &&
        !this.isSnoozed()
      ) {
        await this.prompt(
          false,
          'A new version of SchoolUniverse app is available.',
          platformConfig.store_url
        );
      }
    } catch (error) {
      console.error('App update check failed', error);
    }
  }

  private async prompt(force: boolean, message: string, storeUrl: string): Promise<void> {
    if (this.presenting) {
      return;
    }
    this.presenting = true;

    const alert = await this.alertController.create({
      header: force ? 'Update required' : 'Update available',
      message,
      backdropDismiss: !force,
      buttons: force
        ? [
            {
              text: 'Update',
              handler: () => {
                this.openStore(storeUrl);
                return false;
              },
            },
          ]
        : [
            {
              text: 'Later',
              role: 'cancel',
              handler: () => this.snooze(),
            },
            {
              text: 'Update',
              handler: () => this.openStore(storeUrl),
            },
          ],
    });

    await alert.present();
    await alert.onDidDismiss();
    this.presenting = false;
  }

  private snooze(): void {
    localStorage.setItem(this.snoozeKey, this.today());
  }

  private isSnoozed(): boolean {
    return localStorage.getItem(this.snoozeKey) === this.today();
  }

  private today(): string {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  }

  private async openStore(storeUrl: string): Promise<void> {
    if (!storeUrl) {
      return;
    }
    try {
      await Browser.open({ url: storeUrl });
    } catch (error) {
      window.open(storeUrl, '_system');
    }
  }
}
